import type { ContractSection, ContractTemplate } from '../contract/types';
import { genId } from '../contract/types';
import { DEFAULT_CONTRACT_SETTINGS } from '../utils/constants';

/** 分配式 Omit：对联合类型逐支去键（普通 Omit 会塌成公共键）。 */
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;
type SectionBlueprint = DistributiveOmit<ContractSection, 'id'>;

/**
 * 内置模板库：开箱即用的常见单据。字段按「名称」绑定，套用到任意表时靠
 * 字段名模糊匹配自动适配（见 fieldMatch），无匹配的字段渲染为空、用户改。
 * 每个模板返回「无 id 的区块蓝图」，套用时现生成 id。
 */

export interface LibraryTemplate {
  key: string;
  name: string;
  category: string;
  emoji: string;
  /** 需要的常见字段名（提示用户表里最好有这些字段）。 */
  fields: string[];
  build: () => SectionBlueprint[];
}

// 便捷构造器（省略 id，套用时补）
const h = (content: string, fontSizePx = 22): SectionBlueprint => ({
  type: 'header', content, align: 'center', fontSizePx, bold: true,
});
const grid = (
  columns: 1 | 2,
  items: Array<[string, string]>, // [标签, 字段名]
): SectionBlueprint => ({
  type: 'info_grid',
  columns,
  items: items.map(([label, fieldName]) => ({ id: genId(), label, fieldId: fieldName, staticValue: '' })),
});
const text = (content: string, fontSizePx: number | null = null): SectionBlueprint => ({
  type: 'text_block', content, fontSizePx,
});
const gap = (): SectionBlueprint => ({ type: 'divider', style: 'blank' });
const line = (): SectionBlueprint => ({ type: 'divider', style: 'line' });
const sign = (parties: string[]): SectionBlueprint => ({
  type: 'signature',
  parties: parties.map((label) => ({
    id: genId(),
    label,
    rows: [
      { id: genId(), label: '签字', fieldId: null, staticValue: '' },
      { id: genId(), label: '日期', fieldId: null, staticValue: '' },
    ],
  })),
});
/**
 * 一张框线表：第一行表头（灰底加粗），后面每行是 [标签, {{字段}}] 这种格子内容。
 * 成绩单、工资条、请款单这类「一格一个值」的单据全靠它。
 */
const ftable = (rows: string[][], widths?: number[]): SectionBlueprint => {
  const cols = Math.max(...rows.map((r) => r.length));
  const cell = (content: string, head: boolean) => ({
    id: genId(), content, colSpan: 1, rowSpan: 1, align: 'center' as const, bold: head, shaded: head, merged: false,
  });
  return {
    type: 'free_table',
    colWidths: widths && widths.length === cols ? widths : Array.from({ length: cols }, () => Math.round((1000 / cols)) / 10),
    borderPx: 1,
    fontSizePx: null,
    rows: rows.map((r, i) => ({
      id: genId(),
      heightMm: null,
      cells: Array.from({ length: cols }, (_, c) => cell(r[c] ?? '', i === 0)),
    })),
  };
};
/** 明细表：表头 + 一行样板行，按关联字段自动加行；列绑子表字段名。 */
const detail = (linkField: string, columns: string[]): SectionBlueprint => {
  const cell = (content: string, head: boolean) => ({
    id: genId(), content, colSpan: 1, rowSpan: 1, align: 'center' as const, bold: head, shaded: head, merged: false,
  });
  const columnFields: Record<number, string> = {};
  columns.forEach((c, i) => { columnFields[i] = c; });
  return {
    type: 'free_table',
    colWidths: columns.map(() => Math.round(1000 / columns.length) / 10),
    borderPx: 1,
    fontSizePx: null,
    rows: [
      { id: genId(), heightMm: null, cells: columns.map((c) => cell(c, true)) },
      { id: genId(), heightMm: null, cells: columns.map(() => cell('', false)) },
    ],
    repeat: { source: 'link', linkFieldId: linkField, rowIndex: 1, columnFields },
    summary: null,
  };
};

/** 商城里的分类（照竞品的分法）。库里的细分类别归到这五组。 */
export const STORE_GROUPS = ['采购销售', '人事行政', '财务类', '维修服务', '其他'] as const;
export type StoreGroup = (typeof STORE_GROUPS)[number];
export function storeGroup(category: string): StoreGroup {
  if (['销售', '采购', '仓储'].includes(category)) return '采购销售';
  if (['行政', '人事'].includes(category)) return '人事行政';
  if (category === '财务') return '财务类';
  if (['生产', '维修', '服务'].includes(category)) return '维修服务';
  return '其他';
}

const barcode = (fieldName: string): SectionBlueprint => ({
  type: 'code', codeType: 'barcode', fieldId: fieldName, staticValue: '', sizePx: 56, align: 'right', showText: true,
});

export const LIBRARY: LibraryTemplate[] = [
  {
    key: 'quote', name: '报价单', category: '销售', emoji: '💰',
    fields: ['客户名称', '联系人', '报价日期', '报价编号', '总金额'],
    build: () => [
      h('报价单'),
      grid(2, [['客户名称', '客户名称'], ['联系人', '联系人'], ['报价编号', '报价编号'], ['报价日期', '报价日期']]),
      line(),
      text('合计金额（大写）：{{总金额|大写}}'),
      text('说明：本报价有效期 30 天，最终以双方确认为准。如需明细，添加「明细表格」区块并绑定关联字段。', 12),
      gap(),
      sign(['甲方（客户）', '乙方（我方）']),
    ],
  },
  {
    key: 'delivery', name: '送货单', category: '销售', emoji: '🚚',
    fields: ['客户名称', '收货地址', '送货日期', '送货单号'],
    build: () => [
      h('送货单'),
      grid(2, [['客户名称', '客户名称'], ['送货单号', '送货单号'], ['收货地址', '收货地址'], ['送货日期', '送货日期']]),
      line(),
      text('如需货品明细，添加「明细表格」区块并绑定关联字段。', 12),
      gap(),
      sign(['送货人', '收货人签字']),
    ],
  },
  {
    key: 'receipt', name: '收据', category: '财务', emoji: '🧾',
    fields: ['付款方', '金额', '收款日期', '事由'],
    build: () => [
      h('收　据'),
      grid(1, [['付款方', '付款方'], ['事由', '事由'], ['收款日期', '收款日期']]),
      gap(),
      text('金额（大写）：{{金额|大写}}', 16),
      gap(), gap(),
      sign(['收款人']),
    ],
  },
  {
    key: 'outbound', name: '出库单', category: '仓储', emoji: '📦',
    fields: ['物品名称', '领用人', '部门', '出库日期', '出库单号'],
    build: () => [
      h('物资出库单'),
      barcode('出库单号'),
      grid(2, [['物品名称', '物品名称'], ['出库单号', '出库单号'], ['领用人', '领用人'], ['部门', '部门'], ['出库日期', '出库日期']]),
      line(),
      text('如需物资明细，添加「明细表格」区块并绑定关联字段。', 12),
      gap(),
      sign(['仓库发放', '领用人签字']),
    ],
  },
  {
    key: 'employ_cert', name: '在职证明', category: '行政', emoji: '📄',
    fields: ['姓名', '部门', '职位', '入职日期'],
    build: () => [
      h('在职证明'),
      gap(),
      text('兹证明 {{姓名}} 现于本公司 {{部门}} 担任 {{职位}} 一职，自 {{入职日期}} 至今在职，特此证明。', 15),
      gap(), gap(), gap(),
      grid(1, [['开具日期', '开具日期']]),
      sign(['公司盖章']),
    ],
  },
  {
    key: 'badge', name: '员工工作证', category: '行政', emoji: '🪪',
    fields: ['姓名', '部门', '职位', '工号'],
    build: () => [
      h('工 作 证', 20),
      line(),
      grid(1, [['姓名', '姓名'], ['部门', '部门'], ['职位', '职位'], ['工号', '工号']]),
      gap(),
      barcode('工号'),
    ],
  },
  // ── 下面几张是按线上表的真实字段补的：原来只有销售/财务/行政 6 张，
  // 一张考勤签退表、一张工单表、一张报销表都匹配不上，首屏就什么都推不出来。
  {
    key: 'attendance', name: '考勤签到表', category: '人事', emoji: '🕘',
    fields: ['姓名', '日期', '签到时间', '签退时间', '填写人'],
    build: () => [
      h('考勤签到表'),
      grid(2, [['姓名', '姓名'], ['日期', '日期'], ['签到时间', '签到时间'], ['签退时间', '签退时间']]),
      line(),
      text('说明：本表由 {{填写人}} 填写，签到与签退时间以系统记录为准。', 12),
      gap(),
      sign(['本人确认', '主管审核']),
    ],
  },
  {
    key: 'leave', name: '请假单', category: '人事', emoji: '🗓️',
    fields: ['姓名', '部门', '请假类型', '开始时间', '结束时间', '事由'],
    build: () => [
      h('请 假 单'),
      grid(2, [['姓名', '姓名'], ['部门', '部门'], ['请假类型', '请假类型'], ['天数', '天数'], ['开始时间', '开始时间'], ['结束时间', '结束时间']]),
      line(),
      text('请假事由：{{事由}}', 13),
      gap(), gap(),
      sign(['申请人', '部门主管', '人事审批']),
    ],
  },
  {
    key: 'expense', name: '费用报销单', category: '财务', emoji: '💳',
    fields: ['报销人', '部门', '报销金额', '报销事由', '报销日期'],
    build: () => [
      h('费用报销单'),
      grid(2, [['报销人', '报销人'], ['部门', '部门'], ['报销日期', '报销日期'], ['报销金额', '报销金额']]),
      line(),
      text('报销事由：{{报销事由}}', 13),
      text('金额大写：{{报销金额|大写}}', 13),
      gap(),
      text('如需费用明细，添加「明细表格」区块并绑定关联字段。', 12),
      gap(),
      sign(['报销人', '部门审批', '财务审核']),
    ],
  },
  {
    key: 'inbound', name: '入库单', category: '仓储', emoji: '📥',
    fields: ['物品名称', '数量', '供应商', '入库日期', '入库单号'],
    build: () => [
      h('物资入库单'),
      barcode('入库单号'),
      grid(2, [['入库单号', '入库单号'], ['入库日期', '入库日期'], ['供应商', '供应商'], ['物品名称', '物品名称'], ['数量', '数量']]),
      line(),
      text('如需物资明细，添加「明细表格」区块并绑定关联字段。', 12),
      gap(),
      sign(['送货方', '仓库验收']),
    ],
  },
  {
    key: 'workorder', name: '工单 / 派工单', category: '生产', emoji: '🛠️',
    fields: ['工单号', '客户名称', '任务描述', '负责人', '完成期限'],
    build: () => [
      h('派 工 单'),
      barcode('工单号'),
      grid(2, [['工单号', '工单号'], ['客户名称', '客户名称'], ['负责人', '负责人'], ['完成期限', '完成期限']]),
      line(),
      text('任务描述：{{任务描述}}', 13),
      gap(), gap(),
      sign(['派工人', '接单人', '验收']),
    ],
  },
  {
    key: 'inspection', name: '点检 / 巡检表', category: '生产', emoji: '✅',
    fields: ['设备名称', '点检项', '点检人', '点检日期', '结果'],
    build: () => [
      h('设备点检表'),
      grid(2, [['设备名称', '设备名称'], ['点检日期', '点检日期'], ['点检人', '点检人'], ['结果', '结果']]),
      line(),
      text('点检项：{{点检项}}', 13),
      gap(),
      sign(['点检人', '班组长']),
    ],
  },
  // ── 以下为对齐竞品商城补的一批 ──
  {
    key: 'purchase', name: '采购单', category: '采购', emoji: '🛒',
    fields: ['供应商', '采购日期', '采购单号', '联系人', '联系电话', '总金额', '采购明细'],
    build: () => [
      h('采购单'),
      grid(2, [['供应商', '供应商'], ['采购单号', '采购单号'], ['联系人', '联系人'], ['联系电话', '联系电话'], ['采购日期', '采购日期'], ['交货日期', '交货日期']]),
      detail('采购明细', ['产品名称', '规格', '单价', '数量', '金额']),
      text('合计金额（大写）：{{总金额|大写}}'),
      gap(),
      sign(['采购方', '供应商确认']),
    ],
  },
  {
    key: 'shipment', name: '发货单', category: '销售', emoji: '📦',
    fields: ['客户名称', '客户电话', '收货地址', '发货日期', '发货单号', '发货明细'],
    build: () => [
      h('发货单'),
      grid(2, [['客户名称', '客户名称'], ['客户电话', '客户电话'], ['收货地址', '收货地址'], ['发货日期', '发货日期'], ['发货单号', '发货单号'], ['物流单号', '物流单号']]),
      detail('发货明细', ['产品名称', '型号', '规格', '数量', '备注']),
      gap(),
      sign(['发货人', '收货人签字']),
    ],
  },
  {
    key: 'acceptance', name: '采购验收单', category: '采购', emoji: '✅',
    fields: ['供应商', '采购单号', '验收日期', '验收人', '验收明细'],
    build: () => [
      h('采购验收单'),
      grid(2, [['供应商', '供应商'], ['采购单号', '采购单号'], ['验收日期', '验收日期'], ['验收人', '验收人']]),
      detail('验收明细', ['产品名称', '规格', '应收数量', '实收数量', '验收结果']),
      text('验收结论：{{验收结论}}'),
      gap(),
      sign(['验收人', '供应商代表']),
    ],
  },
  {
    key: 'sales_confirm', name: '销售合同确认单', category: '销售', emoji: '📝',
    fields: ['客户名称', '合同编号', '签订日期', '合同金额', '付款方式', '交付日期'],
    build: () => [
      h('销售合同确认单'),
      grid(2, [['客户名称', '客户名称'], ['合同编号', '合同编号'], ['签订日期', '签订日期'], ['交付日期', '交付日期'], ['付款方式', '付款方式'], ['合同金额', '合同金额']]),
      line(),
      text('合同金额（大写）：{{合同金额|大写}}'),
      text('双方确认以上内容无误，本确认单一式两份，各执一份。', 12),
      gap(),
      sign(['甲方（客户）', '乙方（我方）']),
    ],
  },
  {
    key: 'payment_request', name: '费用请款单', category: '财务', emoji: '💳',
    fields: ['申请单号', '申请时间', '申请人', '申请部门', '付款方式', '收款人', '银行账号', '请款金额', '事由'],
    build: () => [
      h('费用请款单'),
      ftable([
        ['申请单号', '{{申请单号}}', '申请时间', '{{申请时间}}'],
        ['申请人', '{{申请人}}', '申请部门', '{{申请部门}}'],
        ['付款方式', '{{付款方式}}', '收款人', '{{收款人}}'],
        ['银行账号', '{{银行账号}}', '请款金额', '{{请款金额}}'],
      ], [18, 32, 18, 32]),
      text('请款事由：{{事由}}'),
      text('金额（大写）：{{请款金额|大写}}'),
      gap(),
      sign(['申请人', '部门负责人', '财务审核', '总经理']),
    ],
  },
  {
    key: 'pay_apply', name: '付款申请单', category: '财务', emoji: '🏦',
    fields: ['申请人', '申请部门', '申请日期', '付款金额', '收款单位', '开户行', '账号', '付款事由'],
    build: () => [
      h('付款申请单'),
      text('转账支票（ ）  公司网银（ ）  个人网银（ ）', 12),
      ftable([
        ['申请人', '{{申请人}}', '申请部门', '{{申请部门}}'],
        ['申请日期', '{{申请日期}}', '付款金额', '{{付款金额}}'],
        ['收款单位', '{{收款单位}}', '开户行', '{{开户行}}'],
        ['账号', '{{账号}}', '付款事由', '{{付款事由}}'],
      ], [18, 32, 18, 32]),
      text('金额（大写）：{{付款金额|大写}}'),
      gap(),
      sign(['申请人', '部门审批', '财务', '总经理']),
    ],
  },
  {
    key: 'payslip', name: '工资条', category: '财务', emoji: '💴',
    fields: ['姓名', '部门', '岗位', '基本工资', '绩效工资', '应出勤', '全勤奖', '提成', '扣款', '应发工资', '实发工资'],
    build: () => [
      h('工资条', 20),
      text('日期：{{月份}}', 12),
      ftable([
        ['姓名', '部门', '岗位', '基本工资', '绩效工资', '应出勤', '全勤奖', '提成', '扣款', '应发工资', '实发工资'],
        ['{{姓名}}', '{{部门}}', '{{岗位}}', '{{基本工资}}', '{{绩效工资}}', '{{应出勤}}', '{{全勤奖}}', '{{提成}}', '{{扣款}}', '{{应发工资}}', '{{实发工资}}'],
      ]),
      text('本人确认以上工资明细无误。签字：____________', 12),
    ],
  },
  {
    key: 'loan', name: '借款单', category: '财务', emoji: '🧾',
    fields: ['借款人', '部门', '借款日期', '借款金额', '借款事由', '预计还款日期'],
    build: () => [
      h('借款单'),
      ftable([
        ['借款人', '{{借款人}}', '部门', '{{部门}}'],
        ['借款日期', '{{借款日期}}', '预计还款日期', '{{预计还款日期}}'],
        ['借款金额', '{{借款金额}}', '借款事由', '{{借款事由}}'],
      ], [18, 32, 18, 32]),
      text('金额（大写）：{{借款金额|大写}}'),
      gap(),
      sign(['借款人', '部门负责人', '财务', '总经理']),
    ],
  },
  {
    key: 'transcript', name: '学生成绩单', category: '教育', emoji: '🎓',
    fields: ['姓名', '学号', '班级', '语文', '数学', '英语', '总分', '排名'],
    build: () => [
      h('学生成绩单'),
      ftable([
        ['姓名', '{{姓名}}', '学号', '{{学号}}'],
        ['班级', '{{班级}}', '学期', '{{学期}}'],
        ['语文', '{{语文}}', '数学', '{{数学}}'],
        ['英语', '{{英语}}', '总分', '{{总分}}'],
        ['班级排名', '{{排名}}', '评语', '{{评语}}'],
      ], [18, 32, 18, 32]),
      gap(),
      sign(['班主任', '家长签字']),
    ],
  },
  {
    key: 'equip_inspection', name: '设备巡检报告单', category: '维修', emoji: '🔧',
    fields: ['巡检编号', '巡检日期', '设备名称', '设备编号', '巡检人', '巡检结果', '异常描述', '处理措施'],
    build: () => [
      h('设备巡检报告单'),
      ftable([
        ['巡检编号', '{{巡检编号}}', '巡检日期', '{{巡检日期}}'],
        ['设备名称', '{{设备名称}}', '设备编号', '{{设备编号}}'],
        ['巡检人', '{{巡检人}}', '巡检结果', '{{巡检结果}}'],
      ], [18, 32, 18, 32]),
      text('异常描述：{{异常描述}}'),
      text('处理措施：{{处理措施}}'),
      gap(),
      sign(['巡检人', '设备负责人']),
    ],
  },
  {
    key: 'aftersale', name: '售后服务申请单', category: '服务', emoji: '🛠',
    fields: ['售后编号', '申请时间', '客户名称', '联系电话', '产品名称', '购买日期', '问题描述', '处理方式'],
    build: () => [
      h('售后服务申请单'),
      text('售后编号：{{售后编号}}    申请时间：{{申请时间}}', 12),
      ftable([
        ['客户名称', '{{客户名称}}', '联系电话', '{{联系电话}}'],
        ['产品名称', '{{产品名称}}', '购买日期', '{{购买日期}}'],
        ['产品序列号', '{{序列号}}', '处理方式', '{{处理方式}}'],
      ], [18, 32, 18, 32]),
      text('问题描述：{{问题描述}}'),
      gap(),
      sign(['客户签字', '受理人']),
    ],
  },
  {
    key: 'repair', name: '维修工单', category: '维修', emoji: '🔩',
    fields: ['工单号', '报修日期', '报修人', '报修部门', '设备名称', '故障描述', '维修人', '维修结果', '完成日期'],
    build: () => [
      h('维修工单'),
      ftable([
        ['工单号', '{{工单号}}', '报修日期', '{{报修日期}}'],
        ['报修人', '{{报修人}}', '报修部门', '{{报修部门}}'],
        ['设备名称', '{{设备名称}}', '维修人', '{{维修人}}'],
        ['完成日期', '{{完成日期}}', '维修结果', '{{维修结果}}'],
      ], [18, 32, 18, 32]),
      text('故障描述：{{故障描述}}'),
      gap(),
      sign(['报修人确认', '维修人']),
    ],
  },
  {
    key: 'duty', name: '值班表', category: '行政', emoji: '📅',
    fields: ['部门', '值班人', '联系电话', '值班时间'],
    build: () => [
      h('值班表'),
      text('{{月份}}', 12),
      detail('值班安排', ['部门', '值班人', '联系电话', '值班时间']),
      text('值班期间请保持电话畅通，遇突发情况及时上报。', 12),
    ],
  },
  {
    key: 'offer', name: '入职录用通知书（Offer Letter）', category: '人事', emoji: '🎉',
    fields: ['姓名', '岗位', '部门', '入职日期', '试用期', '薪资', '工作地点'],
    build: () => [
      h('入职录用通知书'),
      text('{{姓名}} 女士/先生：'),
      text('经过面试与综合评估，我们很高兴地通知您，您已被我公司正式录用。具体信息如下：'),
      ftable([
        ['岗位', '{{岗位}}', '部门', '{{部门}}'],
        ['入职日期', '{{入职日期}}', '试用期', '{{试用期}}'],
        ['薪资', '{{薪资}}', '工作地点', '{{工作地点}}'],
      ], [18, 32, 18, 32]),
      text('请于入职日期携带身份证、学历证明、离职证明等材料到人事部门报到。期待您的加入！'),
      gap(),
      sign(['人力资源部']),
    ],
  },
  {
    key: 'signin', name: '签到表', category: '行政', emoji: '🖊',
    fields: ['会议名称', '会议时间', '会议地点', '姓名', '部门', '联系电话'],
    build: () => [
      h('签到表'),
      grid(2, [['会议 / 活动名称', '会议名称'], ['时间', '会议时间'], ['地点', '会议地点'], ['主持人', '主持人']]),
      detail('参会人员', ['姓名', '部门', '联系电话', '签到']),
    ],
  },
  {
    key: 'resign_cert', name: '离职证明', category: '人事', emoji: '📄',
    fields: ['姓名', '身份证号', '岗位', '入职日期', '离职日期', '公司名称'],
    build: () => [
      h('离职证明'),
      text('兹证明 {{姓名}}（身份证号：{{身份证号}}）自 {{入职日期}} 起在本公司担任 {{岗位}} 一职，于 {{离职日期}} 与本公司解除劳动关系，双方已办理完毕离职交接手续，无任何劳动争议。'),
      text('特此证明。'),
      gap(),
      sign(['{{公司名称}}（盖章）']),
    ],
  },
  {
    key: 'meeting_minutes', name: '会议纪要', category: '其他', emoji: '🗒',
    fields: ['会议主题', '会议时间', '会议地点', '主持人', '参会人员', '会议内容', '决议事项'],
    build: () => [
      h('会议纪要'),
      ftable([
        ['会议主题', '{{会议主题}}', '会议时间', '{{会议时间}}'],
        ['会议地点', '{{会议地点}}', '主持人', '{{主持人}}'],
        ['参会人员', '{{参会人员}}', '记录人', '{{记录人}}'],
      ], [18, 32, 18, 32]),
      text('会议内容：'),
      text('{{会议内容}}'),
      text('决议事项：'),
      text('{{决议事项}}'),
      gap(),
      sign(['主持人', '记录人']),
    ],
  },
];

/** 把库模板套用成用户的一份新模板（生成全新 id）。 */
export function applyLibraryTemplate(lib: LibraryTemplate, tableId: string): ContractTemplate {
  const now = Date.now();
  const sections: ContractSection[] = lib.build().map((blueprint) => ({
    ...blueprint,
    id: genId(),
  }) as ContractSection);
  return {
    id: genId(),
    tableId,
    name: lib.name,
    sections,
    settings: { ...DEFAULT_CONTRACT_SETTINGS },
    createdAt: now,
    updatedAt: now,
  };
}
